import { sortMembersByType, teamsClient } from '@atlaskit/teams-client';
import { isResultWithPageInfo, type TeamMembership } from '@atlaskit/teams-client/types';

import type { StoreApi } from '../types';

const combineIn = (existing: TeamMembership[], newData: TeamMembership[]) => {
	const addedMemberIds = new Set(newData.map((member) => member.membershipId.memberId));

	return existing
		.filter((member) => !addedMemberIds.has(member.membershipId.memberId))
		.concat(newData);
};

export const getTeamMembers =
	(teamId: string, keepExistingData?: boolean, first: number = 100) =>
	async ({ getState, setState }: StoreApi): Promise<void> => {
		if (!keepExistingData) {
			setState({
				teamMembers: {
					teamId,
					isLoading: true,
					isLoadingNextPage: false,
					hasLoaded: false,
					hasError: false,
					errorType: null,
					members: [],
					requestingToJoinUsers: [],
					membersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
				},
				teamAgents: {
					teamId,
					isLoading: true,
					isLoadingNextPage: false,
					hasLoaded: false,
					hasError: false,
					errorType: null,
					members: [],
					agents: [],
				},
			});
		}
		const { teamMembers } = getState();

		if (teamMembers.teamId !== teamId) {
			return;
		}

		setState({
			teamMembers: {
				...teamMembers,
				isLoadingNextPage: true,
			},
			teamAgents: {
				...getState().teamAgents,
			},
		});

		try {
			const membershipsResponse = await teamsClient.queryTeamMemberships(teamId, ['FULL_MEMBER'], {
				first,
				after: teamMembers.membersPageInfo.cursor ?? undefined,
			});

			const memberships = isResultWithPageInfo<TeamMembership>(membershipsResponse)
				? membershipsResponse.edges
				: membershipsResponse;

			const pageInfo = isResultWithPageInfo<TeamMembership>(membershipsResponse)
				? membershipsResponse.pageInfo
				: { hasNextPage: false, endCursor: null };

			const myMembership = getState().myTeamMembership.membership;
			const myAccountId = myMembership?.membershipId.memberId;

			const membershipsWithExisting = keepExistingData
				? combineIn(teamMembers.members, memberships)
				: memberships;

			const sortedMembers = sortMembersByType(membershipsWithExisting, myAccountId);

			const membersWithoutAgents = sortedMembers.filter(
				(member) => !(member.user?.userType === 'agent'),
			);

			const agents = sortedMembers.filter((member) => member.user?.userType === 'agent');

			setState({
				teamMembers: {
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: false,
					errorType: null,
					members: membersWithoutAgents,
					requestingToJoinUsers: [],
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					membersPageInfo: {
						hasNextPage: pageInfo.hasNextPage,
						cursor: pageInfo.endCursor,
					},
				},
				teamAgents: {
					...getState().teamAgents,
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: false,
					errorType: null,
					members: agents,
				},
			});
		} catch (e) {
			if (getState().teamMembers.teamId !== teamId) {
				return;
			}

			setState({
				teamMembers: {
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: true,
					errorType: e as Error,
					members: [],
					requestingToJoinUsers: [],
					membersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
				},
				teamAgents: {
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: true,
					errorType: e as Error,
					members: [],
					agents: [],
				},
			});
		}
	};
